/**
 * Generate an RFC 4122 v4 UUID.
 *
 * Why this exists instead of `crypto.randomUUID()`: that method is only
 * available in *secure contexts* (HTTPS or localhost). Loading the dev
 * server over a LAN IP (e.g. http://10.0.0.173:5173) is insecure, and
 * `crypto.randomUUID` is undefined — which would crash every DB write.
 *
 * This helper prefers `crypto.randomUUID` when available, falls back to
 * `crypto.getRandomValues` (still cryptographically strong and present in
 * every modern browser), and only uses `Math.random` as a final safety net.
 */
export function uuid(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  if (c && typeof c.getRandomValues === 'function') {
    const b = new Uint8Array(16);
    c.getRandomValues(b);
    // RFC 4122 §4.4: set version (4) and variant (10) bits
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const hex = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    return (
      hex.slice(0, 8) +
      '-' +
      hex.slice(8, 12) +
      '-' +
      hex.slice(12, 16) +
      '-' +
      hex.slice(16, 20) +
      '-' +
      hex.slice(20)
    );
  }
  // Weak fallback — only reached in ancient or sandboxed environments
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
